import { unique } from "@foretagsplatsen/core-helpers/array";
import BudgetItem from "./BudgetItem.js";
import Model from "./Model.js";
import UndefinedAccount from "./UndefinedAccount.js";

/**
 * `Budget` is a domain model class representing a fiscal year budget.
 *
 * A cache of all budget items by dimensionId is maintained for lookup
 * efficiency.
 *
 * @param {string} [spec.id] - Identifier of the budget
 * @param {string} spec.name - Name of the budget
 * @param {FiscalYear} spec.fiscalYear - Fiscal year the budget belongs to
 * @param {boolean} spec.isCurrentBudget - True if this budget is the current budget for its year
 * @param {BudgetItem[]} [spec.items] - Array of budget items
 * @param {boolean} [spec.isVisibleToAllUsers] - Indicates whether this
 *   budget is visible to all users of company. If not, the client users
 *   will not see the budget, in the budgets archive for instance.
 */
export default class Budget extends Model {
	constructor({
		id,
		name,
		fiscalYear,
		isCurrentBudget,
		items = [],
		isVisibleToAllUsers = false,
	} = {}) {
		super(...arguments);
		this._id = id;
		this._name = name;
		this._fiscalYear = fiscalYear;
		this._isCurrent = isCurrentBudget;

		this._itemsByDimensionId = {};
		this._items = [];

		items.forEach((item) => this.addItem(item));

		this._isVisibleToAllUsers = isVisibleToAllUsers;
	}

	getId() {
		return this._id;
	}

	getName() {
		return this._name;
	}

	setName(name) {
		this._name = name;
	}

	getFiscalYear() {
		return this._fiscalYear;
	}

	setFiscalYear(fiscalYear) {
		this._fiscalYear = fiscalYear;
	}

	getFiscalYearId() {
		return this._fiscalYear && this._fiscalYear.getId();
	}

	getFiscalYearMonths() {
		return this.getFiscalYear() && this.getFiscalYear().asMonths();
	}

	getPeriodType() {
		return this._periodType;
	}

	setCurrent(bool) {
		this._isCurrent = bool;
	}

	getMonths() {
		return this._fiscalYear.asMonths();
	}

	isCurrent() {
		return this._isCurrent;
	}

	isEmpty() {
		return this._items.length === 0;
	}

	isNew() {
		return !this._id;
	}

	isForActiveFiscalYear() {
		const year = this.getFiscalYear();

		return year && year.isActive();
	}

	periodName() {
		return this.getFiscalYear().toSmartString();
	}

	hasAccount(account) {
		return this.hasAccountNumber(account.getNumber());
	}

	hasAccountNumber(accountNumber) {
		return this._items.find(
			(item) => item.getAccountNumber() === accountNumber,
		);
	}

	/**
	 * A budget has an amount when at least one of its budget items has an
	 * amount.
	 */
	hasAmount() {
		return this._items.some((item) => item.hasAmount());
	}

	getItems() {
		return this._items;
	}

	/**
	 * Find all budget items for `period` with `accountNumber` and `dimensionId`.
	 *
	 * If `dimensionId` is null or undefined, only look for budget items with no
	 * dimension, otherwise look for a budget item with that dimension id.
	 */
	findItems({ period, accountNumber, dimensionId = "" } = {}) {
		if (!this._itemsByDimensionId[dimensionId]) {
			return [];
		}

		return this._itemsByDimensionId[dimensionId].filter((item) =>
			item.matches({ period, accountNumber }),
		);
	}

	/**
	 * Find a budget item for `period` with `accountNumber` and `dimensionId`.
	 *
	 * If `dimensionId` is null or undefined, only look for budget items with no
	 * dimension, otherwise look for a budget item with that dimension id.
	 */
	findItem({ period, accountNumber, dimensionId = "" } = {}) {
		if (!this._itemsByDimensionId[dimensionId]) {
			return null;
		}

		return this._itemsByDimensionId[dimensionId].find((item) =>
			item.matches({ period, accountNumber }),
		);
	}

	getAccountNumbers() {
		return unique(
			this._items.map((item) => item.getAccountNumber()),
		).sort();
	}

	/**
	 * Return all accounts in the fiscal year for which there's no budget item yet.
	 */
	getUnusedAccounts() {
		return this._fiscalYear
			.getAccounts()
			.filter((account) => !this.hasAccount(account));
	}

	/**
	 * Return all accounts in the fiscal year for which there's no budget item yet for a specific dimension.
	 */
	getUnusedAccountsByDimension(dimensionId = "") {
		return this._fiscalYear.getAccounts().filter(
			(account) =>
				!this.findItem({
					period: this._fiscalYear,
					accountNumber: account.getNumber(),
					dimensionId,
				}),
		);
	}

	/**
	 * Returns the smallest unused account that is bigger than the biggest used
	 * one.
	 */
	getNextUnusedAccount() {
		const biggestAccountNumber = this.getBiggestAccountNumber();
		return this.getUnusedAccounts().find(
			(account) => account.getNumber() > biggestAccountNumber,
		);
	}

	getBiggestAccountNumber() {
		const accountNumbers = this.getAccountNumbers();
		return accountNumbers.at(-1);
	}

	setAmount({ accountNumber, period, amount, dimensionId }) {
		let budgetItem = this.findItem({
			accountNumber,
			period,
			dimensionId,
		});
		if (budgetItem) {
			budgetItem.setAmount(amount);
		} else {
			budgetItem = new BudgetItem({
				accountNumber,
				month: period,
				amount,
				dimensionId,
			});

			this.addItem(budgetItem);
		}
	}

	/**
	 * Add `item` to the list of budget items.
	 *
	 * If another item with for the same account, period and dimensionId is
	 * already present, simply add `item`'s amount to that item.
	 */
	addItem(item) {
		const duplicate = this.findItem({
			period: item.getMonth(),
			accountNumber: item.getAccountNumber(),
			dimensionId: item.getDimensionId(),
		});

		if (duplicate) {
			duplicate.setAmount(duplicate.getAmount() + item.getAmount());
		} else {
			this._items.push(item);

			if (!this._itemsByDimensionId[item.getDimensionId()]) {
				this._itemsByDimensionId[item.getDimensionId()] = [];
			}

			this._itemsByDimensionId[item.getDimensionId()].push(item);
		}
	}

	/**
	 * Return a shallow copy of the receiver. The receiver's id is not carried
	 * over to copies. Copies also have `isCurrent` set to `false`, whether or not
	 * the receiver is the current budget.
	 */
	copy() {
		return new this.constructor({
			name: this._name,
			fiscalYear: this._fiscalYear,
			isCurrentBudget: false,
			items: this._items,
		});
	}

	copyBudgetWithoutItems() {
		const budgetCopy = new this.constructor({
			id: this._id,
			name: this._name,
			fiscalYear: this._fiscalYear,
			isCurrentBudget: this._isCurrent,
		});

		delete budgetCopy._items;

		return budgetCopy;
	}

	/**
	 * Copy all budget items from `otherBudget` and replace current budget
	 * items.
	 *
	 * Copying is done by iterating over all items in `otherBudget` and checking
	 * that the item's period month index is within the receiver's fiscal year.
	 */
	copyItemsFromBudget(otherBudget) {
		this._items = [];

		otherBudget.getItems().forEach((item) => {
			const month = this.getFiscalYear()
				.asMonths()
				.find(
					// eslint-disable-next-line no-shadow -- bad practice, we should fix this
					(month) =>
						month.getPeriodIndex() ===
						item.getMonth().getPeriodIndex(),
				);

			if (!month) {
				return;
			}

			this.addItem(item.cloneForMonth(month));
		});
	}

	removeAllItemsForAccountNumber(accountNumber) {
		this._items = this._items.filter(
			(item) => item.getAccountNumber() !== accountNumber,
		);

		Object.keys(this._itemsByDimensionId).forEach((dimensionId) => {
			this._itemsByDimensionId[dimensionId] = this._itemsByDimensionId[
				dimensionId
			].filter((item) => item.getAccountNumber() !== accountNumber);
		});
	}

	/**
	 * Populate the budget with BudgetItem instances so that for all accounts
	 * for which there is a budget item, the budget has one BudgetItem per month
	 * in its fiscal year.
	 */
	ensureItemsForAllAccounts() {
		this.ensureItemsForAccountNumbers(this.getAccountNumbers());
	}

	/**
	 * Populate the budget with BudgetItem instances for `accountNumber` and
	 * each month in the fiscal year.
	 *
	 * When a `dimensionId` is provided, populate the budget with items for that
	 * dimension id.
	 */
	ensureItemsFor({ accountNumber, dimensionId }) {
		this._fiscalYear.asMonths().forEach((month) => {
			if (this.findItem({ accountNumber, dimensionId, period: month })) {
				return;
			}

			this.addItem(
				new BudgetItem({
					accountNumber,
					dimensionId,
					month,
				}),
			);
		});
	}

	/**
	 * Populate the budget with BudgetItem instances for all `accountNumber`s
	 * and months in the fiscal year.
	 */
	ensureItemsForAccountNumbers(accountNumbers) {
		accountNumbers.forEach((accountNumber) => {
			this.ensureItemsFor({ accountNumber });
		});
	}

	ensureItemsForDimensionId(dimensionId) {
		this.getAccountNumbers().forEach((accountNumber) =>
			this.ensureItemsFor({ accountNumber, dimensionId }),
		);
	}

	computePeriodSum({ period, dimensionId } = {}) {
		return this.computeSum(
			this.findItems({
				period,
				dimensionId,
			}),
		);
	}

	computeSum(budgetItems) {
		return budgetItems.reduce(
			(acc, budgetItem) => acc + budgetItem.getAmount(),
			0,
		);
	}

	computeTotalIncome(chartOfAccounts) {
		return this._computeSumForAccountGroupId("Income", chartOfAccounts);
	}

	computeTotalCosts(chartOfAccounts) {
		return this._computeSumForAccountGroupId("Costs", chartOfAccounts);
	}

	removeEmptyRows() {
		this.getAccountNumbers().forEach((accountNumber) => {
			if (
				this.findItems({ accountNumber }).some((item) =>
					item.hasAmount(),
				)
			) {
				return;
			}
			this.removeAllItemsForAccountNumber(accountNumber);
		});
	}

	computeAccountGroupSum({ accountGroup, period, dimensionId }) {
		const items = this._filterItemsInGroup(accountGroup).filter((item) =>
			item.matches({ period, dimensionId }),
		);

		return this.computeSum(items);
	}

	computeAccountRowSum({ accountNumber, period, dimensionId }) {
		return this.computeSum(
			this.findItems({ accountNumber, period, dimensionId }),
		);
	}

	isVisibleToAllUsers() {
		return this._isVisibleToAllUsers;
	}

	setVisibleToAllUsers(visibleToAllUsers) {
		this._isVisibleToAllUsers = visibleToAllUsers;
	}

	_filterItemsInGroup(accountGroup) {
		return this._items.filter((item) => {
			// We don't need a real account object because we only want to
			// know if the account number is in the chart of accounts:
			const account = new UndefinedAccount({
				number: item.getAccountNumber(),
			});

			return accountGroup.hasAccount(account);
		});
	}

	_computeSumForAccountGroupId(accountGroupId, chartOfAccounts) {
		const accountGroup = chartOfAccounts.findById(accountGroupId);
		const items = this._filterItemsInGroup(accountGroup);
		return this.computeSum(items);
	}

	_serializedProperties() {
		const data = super._serializedProperties();

		data.name = this._name;
		data.isCurrentBudget = this._isCurrent;

		// The fiscal year cannot be changed for existing budgets as they belong
		// to a fiscal year.
		if (this.isNew()) {
			data.fiscalYearId = this.getFiscalYearId();
		}

		// Storing all items for all dimensions can lead to obscenely big budget
		// data.
		// Trim items by removing all items that have no amount (or zero). Those
		// items do not need to be stored.
		if (this._items) {
			data.items = this._items.filter((item) => item.hasAmount());
		}

		data.isVisibleToAllUsers = this._isVisibleToAllUsers;

		return data;
	}

	static transformMaterializationData(data, context) {
		const fiscalYears =
			context.fiscalYears || context.getCompany().getYears();

		data.fiscalYear = fiscalYears.find(
			(year) => year.getId() === data.fiscalYearId,
		);

		// If we do not have enough context (no fiscal year),
		// simply ignore all items
		if (!data.fiscalYear) {
			data.items = [];
			return;
		}

		data.items = BudgetItem.materializeAll(
			data.items,
			context.clone({ fiscalYear: data.fiscalYear }),
		);
	}
}
